<template>
  <div v-if="series.length > 0">
    <apex-chart
      ref="chart"
      :type="type"
      :series="series"
      :options="fullOptions"
      :height="height"
      :width="width"
    />
  </div>
</template>

<script>
/**
 * A component for displaying various types of charts
 * Using apexcharts https://apexcharts.com
 */
import ApexChart from 'vue-apexcharts';

export default {
  name: 'BaseChart',
  components: { ApexChart },
  props: {
    type: {
      type: String,
      default: 'pie',
    },
    series: {
      type: Array,
      default: () => [],
    },
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    height: {
      type: String,
      default: '450px',
    },
    width: {
      type: String,
      default: '100%',
    },
    enrichClick: {
      type: Object,
      default() {
        return {};
      },
    },
    downloadFileName: {
      type: String,
      default: 'Chart',
    },
  },
  data() {
    return {
      isLabelsWrapped: false,
      seriesVisibility: [],
    };
  },
  computed: {
    chartEvents() {
      const pointSelectionCharts = ['donut', 'pie', 'polarArea', 'bar'];
      if (pointSelectionCharts.includes(this.type)) {
        return {
          dataPointSelection: this.handleDataPointSelection,
          legendClick: this.handleLegendClick,
        };
      } else {
        return {
          click: this.handleClick,
          legendClick: this.handleLegendClick,
        };
      }
    },
    fullOptions() {
      const baseOptions = {
        legend: {
          position: 'bottom',
          labels: {
            colors: ['black'],
          },
        },
        plotOptions: {
          bar: {
            dataLabels: {
              position: 'top',
            },
          },
        },
        chart: {
          toolbar: {
            show: false,
            tools: {
              download: false,
              selection: false,
              zoom: false,
              zoomin: false,
              zoomout: false,
              reset: false,
              pan: false,
            },
            export: {
              csv: {
                filename: this.downloadFileName,
              },
              svg: {
                filename: this.downloadFileName,
              },
              png: {
                filename: this.downloadFileName,
              },
            },
          },
          events: this.chartEvents,
        },
      };
      if (this.type === 'bar') {
        baseOptions.dataLabels = {
          enabled: false,
          style: {
            colors: ['#333'],
          },
          offsetY: -20,
          orientation: 'vertical',
        };
        baseOptions.xaxis = {
          labels: {
            rotate: -45,
            rotateAlways: true,
            trim: true,
          },
        };
        // Disable hiding dataSeries when legend clicked
        baseOptions.legend = {
          onItemClick: {
            toggleDataSeries: false,
          },
          onItemHover: {
            highlightDataSeries: true,
          },
        };
        baseOptions.tooltip = {
          followCursor: true,
          shared: true,
          intersect: false,
        };
      }
      if (this.options.chart !== undefined) {
        if (
          this.options.chart.toolbar &&
          typeof this.options.chart.toolbar == 'object'
        ) {
          baseOptions.chart.toolbar = this.options.chart.toolbar;
        }
      }
      this.options.chart
        ? Object.assign(this.options.chart, baseOptions.chart)
        : '';
      return Object.assign({}, baseOptions, this.options);
    },
  },
  watch: {
    options: {
      handler: function () {
        // running this handler for area charts messes us up render
        if (this.type === 'area') return;
        if (this && this.$refs && this.$refs.chart) {
          this.$refs.chart.refresh();
        }
      },
      deep: true,
    },
    'options.xaxis': {
      deep: true,
      handler() {
        if (this.type == 'bar' && !this.isLabelsWrapped) {
          this.options.xaxis.categories = this.options.xaxis.categories.map(
            (label) => this.wordWrapToStringList(label, 25)
          );
          this.isLabelsWrapped = true;
          if (this && this.$refs && this.$refs.chart) {
            this.$refs.chart.refresh();
          }
        }
      },
    },
  },
  created() {
    if (this.type == 'line') {
      for (
        var seriesIndex = 0;
        seriesIndex < this.series.length;
        seriesIndex++
      ) {
        this.seriesVisibility[seriesIndex] = 1;
      }
    }
  },
  methods: {
    wordWrapToStringList(text, maxLength) {
      let result = [],
        line = [];
      let length = 0;
      let splittedTextArray = text.split(' ');
      splittedTextArray.forEach((word, index) => {
        if (word.length > maxLength) {
          result.push(word.slice(0, maxLength));
          splittedTextArray[index + 1] =
            word.slice(maxLength) + ' ' + splittedTextArray[index + 1];
          length = 0;
          return;
        }
        if (length + word.length >= maxLength) {
          result.push(line.join(' '));
          line = [];
          length = 0;
        }
        length += word.length + 1;
        line.push(word);
      });
      if (line.length > 0) {
        result.push(line.join(' '));
      }
      return result;
    },
    refreshChart() {
      this.$refs.chart.refresh();
    },
    handleLegendClick(chartContext, seriesIndex) {
      const label = this.series[seriesIndex].name;
      const eventData = {
        value: label,
        label,
        series: label,
        seriesIndex,
        enrichment: this.enrichClick,
      };
      if (this.seriesVisibility[seriesIndex] == 0) {
        this.seriesVisibility[seriesIndex] = 1;
      } else {
        this.seriesVisibility[seriesIndex] = 0;
      }
      if (this.$listeners['legend-clicked'] !== undefined) {
        this.$emit('legend-clicked', eventData);
      } else {
        this.$emit('clicked', eventData);
      }
    },
    handleDataPointSelection(event, chartContext, config) {
      const { dataPointIndex, seriesIndex } = config;
      this.$emit('clicked', {
        value: this.series[dataPointIndex],
        label: this.options.labels[dataPointIndex],
        series:
          Object.keys(this.series[seriesIndex]).length > 0
            ? this.series[seriesIndex].name
            : 'default', // only one series in these
        seriesIndex,
        dataPointIndex,
        enrichment: this.enrichClick,
      });
    },
    getSeriesName(seriesIndex) {
      //with 3 lines in chart, apexchart may return invalid series index
      // Use one line only to get exact series name as a workaround for now.
      let lineCount = 0;

      for (let i = 0; i < this.seriesVisibility.length; i++) {
        if (this.seriesVisibility[i] === 1) {
          lineCount++;
        }
      }
      if (lineCount === 1) {
        for (let i = 0; i < this.seriesVisibility.length; i++) {
          if (this.seriesVisibility[i] === 1) {
            return this.series[i].name;
          }
        }
      }

      return this.series[seriesIndex].name;
    },
    handleClick(event, chartContext, config) {
      const { seriesIndex, dataPointIndex } = config;
      let value = undefined;
      let series = undefined;
      if (typeof this.series[seriesIndex] === 'object') {
        value = 0;
        if (config.config.series[seriesIndex].data.length) {
          value = this.series[seriesIndex].data[dataPointIndex];
        }
        series = this.getSeriesName(seriesIndex);
      } else {
        value = this.series[dataPointIndex];
        series = 'default';
      }

      const label = this.options.labels
        ? this.options.labels[dataPointIndex]
        : undefined;
      if (value === undefined && label === undefined) {
        console.warn(
          `Unable to get value and label, this may not be an issue is this was a legend click`
        );
        return;
      }

      this.$emit('clicked', {
        value,
        label,
        series,
        seriesIndex,
        dataPointIndex,
        enrichment: this.enrichClick,
      });
    },
  },
};
</script>

<style>
g:hover {
  cursor: pointer;
}
</style>
